/**
 * Account Service
 * Handles vendor account data extraction from expenses
 */

import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Get unique vendors with their totals from expenses
 */
export const getVendorAccounts = async (userId) => {
  try {
    const q = query(
      collection(db, 'expenses'),
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    const expenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Group expenses by vendor
    const vendorMap = new Map();

    expenses.forEach(expense => {
      const vendorName = expense.vendor?.trim() || 'Unknown Vendor';

      if (!vendorMap.has(vendorName)) {
        vendorMap.set(vendorName, {
          name: vendorName,
          totalAmount: 0,
          totalPaid: 0,
          expenseCount: 0,
          expenses: [],
        });
      }

      const vendor = vendorMap.get(vendorName);
      vendor.totalAmount += expense.amount || 0;
      vendor.expenseCount += 1;
      // If expense is marked as reimbursed or has a paid status, count as paid
      if (expense.status === 'reimbursed' || expense.status === 'billed') {
        vendor.totalPaid += expense.amount || 0;
      }
      vendor.expenses.push(expense);
    });

    // Convert to array and calculate balance
    const vendors = Array.from(vendorMap.values()).map(vendor => ({
      ...vendor,
      balance: vendor.totalAmount - vendor.totalPaid,
    }));

    // Sort by total amount descending
    vendors.sort((a, b) => b.totalAmount - a.totalAmount);

    return vendors;
  } catch (error) {
    console.error('Error getting vendor accounts:', error);
    throw error;
  }
};

/**
 * Get expenses for a specific vendor with running balance
 */
export const getVendorExpenses = async (userId, vendorName) => {
  try {
    const q = query(
      collection(db, 'expenses'),
      where('userId', '==', userId),
      orderBy('date', 'asc')
    );

    const snapshot = await getDocs(q);
    const allExpenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Filter by vendor name
    const vendorExpenses = allExpenses.filter(expense => {
      const expVendor = expense.vendor?.trim() || 'Unknown Vendor';
      return expVendor === vendorName;
    });

    // Calculate running balance
    let runningBalance = 0;
    const expensesWithBalance = vendorExpenses.map(expense => {
      runningBalance += expense.amount || 0;
      const paid = expense.status === 'reimbursed' || expense.status === 'billed'
        ? expense.amount
        : 0;
      runningBalance -= paid;

      return {
        ...expense,
        paid,
        balance: runningBalance,
      };
    });

    // Reverse to show most recent first
    return expensesWithBalance.reverse();
  } catch (error) {
    console.error('Error getting vendor expenses:', error);
    throw error;
  }
};

/**
 * Subscribe to expenses for real-time updates
 */
export const subscribeToVendorAccounts = (userId, callback) => {
  const q = query(
    collection(db, 'expenses'),
    where('userId', '==', userId),
    orderBy('date', 'desc')
  );

  return onSnapshot(q, async (snapshot) => {
    const expenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Group expenses by vendor
    const vendorMap = new Map();

    expenses.forEach(expense => {
      const vendorName = expense.vendor?.trim() || 'Unknown Vendor';

      if (!vendorMap.has(vendorName)) {
        vendorMap.set(vendorName, {
          name: vendorName,
          totalAmount: 0,
          totalPaid: 0,
          expenseCount: 0,
          expenses: [],
        });
      }

      const vendor = vendorMap.get(vendorName);
      vendor.totalAmount += expense.amount || 0;
      vendor.expenseCount += 1;
      if (expense.status === 'reimbursed' || expense.status === 'billed') {
        vendor.totalPaid += expense.amount || 0;
      }
      vendor.expenses.push(expense);
    });

    // Convert to array and calculate balance
    const vendors = Array.from(vendorMap.values()).map(vendor => ({
      ...vendor,
      balance: vendor.totalAmount - vendor.totalPaid,
    }));

    vendors.sort((a, b) => b.totalAmount - a.totalAmount);

    callback(vendors);
  });
};
